import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { SvgAdd, SvgCog } from '@actual-app/components/icons/v1';
import { styles } from '@actual-app/components/styles';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';
import { Resizable } from 're-resizable';

import { FeatureErrorFallback } from '#components/FeatureErrorFallback';
import { useLocalPref } from '#hooks/useLocalPref';
import { replaceModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { Accounts } from './Accounts';
import { BudgetName } from './BudgetName';
import { Item } from './Item';
import { PrimaryButtons } from './PrimaryButtons';
import { SecondaryButtons } from './SecondaryButtons';
import { useSidebar } from './SidebarProvider';
import { SidebarUtilities } from './SidebarUtilities';
import { ToggleButton } from './ToggleButton';

export function Sidebar() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sidebar = useSidebar();
  const { width } = useResponsive();
  const isCollapsed = sidebar.collapsed;

  const [sidebarWidthLocalPref, setSidebarWidthLocalPref] =
    useLocalPref('sidebarWidth');
  const DEFAULT_SIDEBAR_WIDTH = 240;
  const COLLAPSED_SIDEBAR_WIDTH = 56;
  const MAX_SIDEBAR_WIDTH = width / 3;
  const MIN_SIDEBAR_WIDTH = 200;

  const [sidebarWidth, setSidebarWidth] = useState(
    Math.min(
      MAX_SIDEBAR_WIDTH,
      Math.max(
        MIN_SIDEBAR_WIDTH,
        sidebarWidthLocalPref || DEFAULT_SIDEBAR_WIDTH,
      ),
    ),
  );
  const onToggleCollapse = () => {
    sidebar.setCollapsed(!isCollapsed);
  };

  const onAddAccount = () => {
    dispatch(replaceModal({ modal: { name: 'add-account', options: {} } }));
  };

  return (
    <ErrorBoundary FallbackComponent={FeatureErrorFallback}>
      <Resizable
        size={{
          width: isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : sidebarWidth,
          height: '100%',
        }}
        onResize={(_event, _direction, elementRef) => {
          setSidebarWidth(elementRef.offsetWidth);
        }}
        onResizeStop={(_event, _direction, elementRef) => {
          setSidebarWidthLocalPref(elementRef.offsetWidth);
        }}
        maxWidth={isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : MAX_SIDEBAR_WIDTH}
        minWidth={isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : MIN_SIDEBAR_WIDTH}
        style={{
          overflow: 'hidden',
        }}
        enable={{
          top: false,
          right: !isCollapsed,
          bottom: false,
          left: false,
          topRight: false,
          bottomRight: false,
          bottomLeft: false,
          topLeft: false,
        }}
      >
        <View
          className={css({
            color: theme.sidebarItemText,
            height: '100%',
            backgroundColor: theme.sidebarBackground,
            borderRight: '1px solid ' + theme.sidebarBorder,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
            ...styles.darkScrollbar,
          })}
        >
          <BudgetName>
            {!sidebar.alwaysFloats && (
              <ToggleButton
                isCollapsed={isCollapsed}
                onToggle={onToggleCollapse}
              />
            )}
          </BudgetName>

          <View
            style={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <PrimaryButtons />

            <Accounts />

            <View style={{ flex: 1 }} />

            <SidebarUtilities />

            {isCollapsed ? (
              <View
                style={{
                  marginBottom: 12,
                  flexShrink: 0,
                  alignItems: 'center',
                }}
              >
                <Item
                  title={t('Add account')}
                  Icon={SvgAdd}
                  onClick={onAddAccount}
                  dataTestId="sidebar-add-account"
                />
                <Item title={t('Settings')} Icon={SvgCog} to="/settings" />
              </View>
            ) : (
              <SecondaryButtons
                buttons={[
                  {
                    title: t('Add account'),
                    Icon: SvgAdd,
                    onClick: onAddAccount,
                    dataTestId: 'sidebar-add-account',
                  },
                ]}
              />
            )}
          </View>
        </View>
      </Resizable>
    </ErrorBoundary>
  );
}
